import type React from "react";
import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { SWReactIcons } from "@similarweb/icons";
import type { INavItemBadgeType } from "@similarweb/ui-components";
import { Tabs, TabList, Tab, TabPanel, NavItemBadge } from "@similarweb/ui-components";

import { BADGE_ITEMS } from "app/components/sidebar3/types";
import { translate } from "app/filters/i18nFilter";
import { openStateUnlockModal } from "app/services/ModalService";
import { isHidden, isLocked } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { WebAnalysisPagesContainer } from "app/pages/website-analysis/pages/WebAnalysisPagesContainer";
import WebAnalysisSubdomainsContainer from "app/pages/website-analysis/subdomains/WebAnalysisSubdomainsContainer";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { useWebsiteContentTouchHookDummyPage } from "./website-content-locked/isWebsiteContentTouchHookDummyPage";
import { PreviewInteractionBlocker } from "./website-content-locked/PreviewInteractionBlocker";

import { Folders as FoldersComponent } from "./Folders/Folders";
import FoldersTable from "./Folders/components/FoldersTable/FoldersTable";
import LineOfBusiness from "./LineOfBusiness/LineOfBusiness";
import { ItemContainer } from "./styledComponent";

export enum ECategoryWebsiteContentType {
    Subdomains = "Subdomains",
    Folders = "Folders",
    PopularPages = "PopularPages",
    LOB = "LOB",
}

interface TabConfig {
    id: ECategoryWebsiteContentType;
    titleKey: string;
    Component: React.ComponentType;
    stateName: string;
    isLocked?: boolean;
    isHidden?: boolean;
    hook?: { name: string };
    badge?: INavItemBadgeType;
}

const getTabConfig = (): TabConfig[] => [
    {
        id: ECategoryWebsiteContentType.Folders,
        titleKey: "analysis.content.folders.title",
        Component: FoldersComponent,
        stateName: "digitalsuite_website_folders",
        isLocked: isLocked(swSettings.components.WsWebContentFolders),
        isHidden: isHidden(swSettings.components.WsWebContentFolders),
        hook: { name: "digitalsuite_website_folders" },
    },
    {
        id: ECategoryWebsiteContentType.LOB,
        titleKey: "analysis.content.lob.title",
        Component: LineOfBusiness,
        stateName: "digitalsuite_website_lob",
        isLocked: isLocked(swSettings.components.WebsiteLOBs),
        isHidden: isHidden(swSettings.components.WebsiteLOBs),
        hook: { name: "digitalsuite_website_content_lob" },
        badge: BADGE_ITEMS.NEW,
    },
    {
        id: ECategoryWebsiteContentType.PopularPages,
        titleKey: "analysis.content.popular.title",
        Component: WebAnalysisPagesContainer,
        stateName: "digitalsuite_website_popular_v2",
        isLocked: isLocked(swSettings.components.PopularPagesV2),
        isHidden: isHidden(swSettings.components.PopularPagesV2),
        hook: { name: "digitalsuite_website_popular_v2" },
    },

    {
        id: ECategoryWebsiteContentType.Subdomains,
        titleKey: "analysis.content.subdomains.title",
        Component: WebAnalysisSubdomainsContainer,
        stateName: "digitalsuite_website_subdomains",
        isLocked: isLocked(swSettings.components.WsWebContentSubdomains),
        isHidden: isHidden(swSettings.components.WsWebContentSubdomains),
        hook: { name: "digitalsuite_website_subdomains" },
    },
];

export const WebsiteContent: React.FC = () => {
    const { params } = useCurrentStateAndParams();
    const isLockedDummyPage = useWebsiteContentTouchHookDummyPage();
    const defaultTab = ECategoryWebsiteContentType.Folders;

    const visibleTabs = useMemo(() => {
        const tabs = getTabConfig().filter((tab) => !tab.isHidden);

        if (!isLockedDummyPage) {
            return tabs;
        }

        if (tabs.some((tab) => tab.id === ECategoryWebsiteContentType.Folders)) {
            return tabs;
        }

        const foldersTab = getTabConfig().find((tab) => tab.id === ECategoryWebsiteContentType.Folders);
        return foldersTab ? [foldersTab, ...tabs] : tabs;
    }, [isLockedDummyPage]);

    const selectedTab = params.selectedTab as ECategoryWebsiteContentType | undefined;

    const activeTabIndex = useMemo(() => {
        const activeTabId = selectedTab ?? defaultTab;
        const index = visibleTabs.findIndex((tab) => tab.id === activeTabId);
        return index >= 0 ? index : 0;
    }, [selectedTab, visibleTabs, defaultTab]);

    const handleTabSelect = (index: number) => {
        if (isLockedDummyPage) {
            return;
        }

        const { id, stateName, isLocked, hook } = visibleTabs[index];
        allTrackers.dispatchTrackEvent("Button", "click", `Website Content Tab/${id}`);
        if (isLocked) {
            openStateUnlockModal(hook);
        } else {
            proNavigator.go(stateName, { ...params, selectedTab: id });
        }
    };

    return (
        <Tabs
            selectedIndex={activeTabIndex}
            onSelect={handleTabSelect}
        >
            <FlexColumn gap="24px">
                <TabList>
                    {visibleTabs.map(({ id, titleKey, isLocked, badge }) => {
                        const isFoldersTab = id === ECategoryWebsiteContentType.Folders;
                        const tabContent = (
                            <Tab key={id}>
                                <ItemContainer>
                                    <span>{translate(titleKey)}</span>
                                    {isLocked && !isLockedDummyPage ? (
                                        <SWReactIcons
                                            iconName="spark"
                                            size="xs"
                                        />
                                    ) : null}
                                    {badge ? <NavItemBadge type={badge} /> : null}
                                </ItemContainer>
                            </Tab>
                        );

                        if (isLockedDummyPage && !isFoldersTab) {
                            return (
                                <PreviewInteractionBlocker
                                    key={id}
                                    display="inline-flex"
                                    data-automation={`website-content-tab-blocker-${id}`}
                                >
                                    {tabContent}
                                </PreviewInteractionBlocker>
                            );
                        }

                        return tabContent;
                    })}
                </TabList>
                {visibleTabs.map(({ id, Component }, index) => {
                    const isActivePanel = index === activeTabIndex;

                    return (
                        <TabPanel key={id}>
                            {isActivePanel ? (
                                isLockedDummyPage ? (
                                    id === ECategoryWebsiteContentType.Folders ? (
                                        <FoldersTable />
                                    ) : null
                                ) : (
                                    <Component />
                                )
                            ) : null}
                        </TabPanel>
                    );
                })}
            </FlexColumn>
        </Tabs>
    );
};
